import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

const GENRE_ROUTES = [
  { label: 'National News', to: '/national-news', color: '#dd3333' },
  { label: 'State News',    to: '/state-news',    color: '#4db2ec' },
  { label: 'Sports',        to: '/sports',        color: '#e67e22' },
  { label: 'Technology',    to: '/technology',    color: '#8e44ad' },
  { label: 'Local News',    to: '/local-news',    color: '#27ae60' },
];

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function goArticle(navigate, article, token) {
  const dest = `/${encodeURIComponent(article.company_name)}?a=${article._id}`;
  if (!token) { navigate(`/login?next=${encodeURIComponent(dest)}`); return; }
  navigate(dest);
}

/* Hero card — fills its container height */
function HeroCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', height: '600px', position: 'relative', overflow: 'hidden', background: '#111' }}>
      {article.headline_image
        ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.5s', transform: hov ? 'scale(1.04)' : 'scale(1)' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '4rem', opacity: 0.1 }}>📰</span>
          </div>
      }
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.35) 55%, transparent 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, padding: '28px', right: 0 }}>
        <div style={{ display: 'inline-block', fontSize: '10px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: color, padding: '3px 8px', marginBottom: '10px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '30px', fontWeight: '900', color: T.white, lineHeight: '37px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* Side article card for the 3-card column beside the hero */
function SideHeroCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ flex: 1, cursor: 'pointer', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: `1px solid ${T.border}` }}>
      <div style={{ flex: '0 0 115px', overflow: 'hidden', background: '#222' }}>
        {article.headline_image
          ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.35s', transform: hov ? 'scale(1.05)' : 'scale(1)' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '2rem', opacity: 0.1 }}>📰</span>
            </div>
        }
      </div>
      <div style={{ flex: 1, padding: '10px 14px', background: T.white, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: '10px', color, fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '22px', transition: 'color 0.14s', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '5px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* Grid card — More Stories section */
function GridCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        cursor: 'pointer',
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '6px',
        overflow: 'hidden',
        boxShadow: hov ? '0 6px 18px rgba(0,0,0,0.13)' : '0 1px 4px rgba(0,0,0,0.07)',
        transition: 'box-shadow 0.2s',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* card-img-top */}
      <div style={{ aspectRatio: '16/9', overflow: 'hidden', flexShrink: 0, background: '#e8e8e8' }}>
        {article.headline_image
          ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.4s', transform: hov ? 'scale(1.04)' : 'scale(1)' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ opacity: 0.18, fontSize: '2.5rem' }}>📰</span>
            </div>
        }
      </div>
      {/* card-body */}
      <div style={{ padding: '0.75rem 0.9rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '5px', fontFamily: 'Arial, sans-serif' }}>
            {article.genre_tree?.main}
          </div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '21px', transition: 'color 0.14s', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Georgia, serif' }}>
            {article.title}
          </div>
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '8px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* Trending horizontal scroll card */
function TrendCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', width: '200px', flexShrink: 0 }}>
      <div style={{ height: '130px', overflow: 'hidden', marginBottom: '8px', background: '#f0f0f0' }}>
        {article.headline_image
          ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.35s', transform: hov ? 'scale(1.05)' : 'scale(1)' }} />
          : <div style={{ width: '100%', height: '100%' }} />
        }
      </div>
      <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', fontFamily: 'Arial, sans-serif' }}>{article.genre_tree?.main}</div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '19px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Georgia, serif' }}>
        {article.title}
      </div>
    </div>
  );
}

/* Sidebar article card — local news + recommended */
function SideCard({ article, onClick }) {
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', gap: '8px', cursor: 'pointer', paddingBottom: '10px', borderBottom: `1px solid ${T.border}`, marginBottom: '10px' }}>
      {article.headline_image && (
        <div style={{ width: '60px', height: '45px', flexShrink: 0, overflow: 'hidden' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '13px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '18px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', transition: 'color 0.12s', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '2px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* Skeleton placeholder block */
function Skeleton({ h = 160, w = '100%', style = {} }) {
  return <div style={{ height: h, width: w, background: '#ececec', borderRadius: '3px', ...style }} />;
}

/* ── Main component ── */
export default function LandingPage5() {
  const navigate = useNavigate();

  const [genreArticles, setGenreArticles]     = useState({});
  const [companies, setCompanies]             = useState([]);
  const [forYouArticles, setForYouArticles]   = useState([]);
  const [forYouHasSignal, setForYouHasSignal] = useState(null);
  const [tokenBalance, setTokenBalance]       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [now, setNow]                         = useState(new Date());
  const [tokenDropOpen, setTokenDropOpen]     = useState(false);
  const [publishersExpanded, setPublishersExpanded]     = useState(false);
  const [trendingArticles, setTrendingArticles]         = useState([]);
  const [newsletterEmail, setNewsletterEmail]           = useState('');
  const [newsletterLoading, setNewsletterLoading]       = useState(false);
  const [newsletterDone, setNewsletterDone]             = useState(false);
  const [newsletterError, setNewsletterError]           = useState('');
  const [isMobile, setIsMobile]                         = useState(() => window.innerWidth < 768);

  // Source recommendation modal
  const [recOpen, setRecOpen]         = useState(false);
  const [recName, setRecName]         = useState('');
  const [recUrl, setRecUrl]           = useState('');
  const [recNote, setRecNote]         = useState('');
  const [recLoading, setRecLoading]   = useState(false);
  const [recError, setRecError]       = useState('');
  const [recSuccess, setRecSuccess]   = useState(false);

  const openRec = () => {
    if (!authToken || userType !== 'user') { navigate(`/login?next=${encodeURIComponent('/')}`); return; }
    setRecOpen(true); setRecName(''); setRecUrl(''); setRecNote(''); setRecError(''); setRecSuccess(false);
  };
  const submitRec = async () => {
    if (!recName.trim() && !recUrl.trim()) { setRecError('Please enter a source name or URL.'); return; }
    setRecLoading(true); setRecError('');
    try {
      await axios.post('/api/users/me/recommend-source', { sourceName: recName, sourceUrl: recUrl, note: recNote }, { headers: { Authorization: `Bearer ${authToken}` } });
      setRecSuccess(true);
    } catch (err) {
      setRecError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setRecLoading(false);
    }
  };

  const tokenDropRef = useRef(null);


  const { accessToken: authToken } = useAuth();
  const userType    = localStorage.getItem('userType');
  const username    = localStorage.getItem('username');
  const companySlug = localStorage.getItem('companyName');
  const isAdmin     = localStorage.getItem('isAdmin') === 'true';

  /* Live clock — refresh every 30s */
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  /* Mobile breakpoint */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* Redirect admin/company/author/editor to their own hub */
  useEffect(() => {
    if (!authToken) return;
    if (isAdmin)                { navigate('/admin', { replace: true }); return; }
    if (userType === 'company') { navigate(`/adminHUD/${encodeURIComponent(companySlug)}`, { replace: true }); return; }
    if (userType === 'author')  { navigate(`/${encodeURIComponent(companySlug)}/author/${encodeURIComponent(username)}`, { replace: true }); return; }
    if (userType === 'editor')  { navigate(`/${encodeURIComponent(companySlug)}/editor/${encodeURIComponent(username)}`, { replace: true }); return; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Close token dropdown on outside click */
  useEffect(() => {
    if (!tokenDropOpen) return;
    const handler = (e) => {
      if (tokenDropRef.current && !tokenDropRef.current.contains(e.target)) setTokenDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tokenDropOpen]);

  /* Data fetch */
  useEffect(() => {
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    axios.get('/api/articles/trending').then(r => setTrendingArticles(r.data || [])).catch(() => {});

    Promise.all([
      axios.get('/api/articles/landing', { headers }),
      axios.get('/api/companies/showcase'),
    ]).then(([artRes, coRes]) => {
      setGenreArticles(artRes.data || {});
      setCompanies((coRes.data || []).sort((a, b) => b.articleCount - a.articleCount));
    }).catch(() => {}).finally(() => setLoading(false));

    if (authToken && userType === 'user') {
      axios.get('/api/users/me', { headers }).then(r => setTokenBalance(r.data.tokens ?? 0)).catch(() => {});
      axios.get('/api/articles/for-you?limit=5', { headers }).then(r => {
        setForYouArticles(r.data.articles || []);
        setForYouHasSignal(r.data.hasSignal);
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Article arrays */
  const allArticles = Object.values(genreArticles).flat();
  const heroArticle = allArticles.find(a => a.headline_image) || allArticles[0] || null;
  const heroSideArticles = allArticles.filter(a => a._id !== heroArticle?._id).slice(0, 3);
  const usedIds = new Set([heroArticle?._id, ...heroSideArticles.map(a => a._id)]);
  const moreArticles  = allArticles.filter(a => !usedIds.has(a._id)).slice(0, 6);
  const localArticles = (genreArticles['Local News'] || []).slice(0, 3);


  /* My Silo href */
  const siloHref = (() => {
    if (!authToken) return null;
    if (isAdmin)                return '/admin';
    if (userType === 'company') return `/adminHUD/${encodeURIComponent(companySlug)}`;
    if (userType === 'author')  return `/${encodeURIComponent(companySlug)}/author/${encodeURIComponent(username)}`;
    if (userType === 'editor')  return `/${encodeURIComponent(companySlug)}/editor/${encodeURIComponent(username)}`;
    if (userType === 'user')    return `/${username}`;
    return null;
  })();

  const go = (article) => goArticle(navigate, article, authToken);

  return (
    <>
    <Helmet>
      <title>PubSilo — The Content Marketplace</title>
      <meta name="description" content="The marketplace where publishers of every size earn from their work — and readers pay only for what they actually read." />
    </Helmet>
    <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Georgia', serif", color: T.bodyText }}>

      {/* ── Navbar ── */}
      {isMobile ? (
        <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          {/* Mobile row 1: logo | branding */}
          <div style={{ display: 'flex', height: '70px' }}>
            <div style={{ width: '33%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
            </div>
            <div style={{ flex: 1, background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ color: T.white, fontSize: '20px', fontWeight: '700', fontFamily: 'Georgia, serif', letterSpacing: '-0.01em', lineHeight: 1 }}>PubSilo</div>
              <div style={{ color: '#aaa', fontSize: '10px', fontFamily: 'Arial, sans-serif', marginTop: '3px' }}>
                {now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>
          {/* Mobile row 2: secondary nav */}
          <div style={{ background: '#0d1f33', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', borderTop: '1px solid #1a2f45' }}>
            {authToken ? (
              <>
                {userType === 'user' && tokenBalance !== null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#1e1e1e', border: '1px solid #333', borderRadius: '5px', padding: '5px 10px', color: T.white, fontSize: '12px', fontFamily: 'Arial, sans-serif' }}
                    onClick={() => navigate('/tokens/purchase')}
                  >
                    <span>🪙</span>
                    <span style={{ fontWeight: '700' }}>{tokenBalance}</span>
                    <span style={{ fontSize: '10px', color: T.accent, marginLeft: '2px', fontWeight: '700' }}>+ Buy</span>
                  </div>
                )}
                {siloHref && (
                  <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '6px 14px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}>
                    {userType === 'company' ? 'Admin Page' : 'My Silo'}
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 12px', border: '1px solid #3a3a3a', borderRadius: '4px' }}>
                  Log In
                </Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 14px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </nav>
      ) : (
      <nav style={{
        background: T.header,
        display: 'grid',
        gridTemplateColumns: '220px 1fr 300px',
        gridTemplateRows: '110px 36px',
        position: 'sticky', top: 0, zIndex: 1000,
        boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
      }}>

        {/* Logo — spans both rows, full height */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
          <img
            src={orangeLogo} alt="PubSilo"
            style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          />
        </div>

        {/* Center: date + live time */}
        <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.03em', marginBottom: '4px' }}>
              {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            <div style={{ color: T.white, fontSize: '44px', fontWeight: '300', fontFamily: "'Arial', sans-serif", letterSpacing: '0.08em', lineHeight: 1 }}>
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>

        {/* Auth column — spans both rows, aligns with right sidebar */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#13273F' }}>
          {authToken ? (
            <>
              {userType === 'user' && tokenBalance !== null && (
                <div ref={tokenDropRef} style={{ position: 'relative' }}>
                  <div
                    onClick={() => setTokenDropOpen(o => !o)}
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '6px', padding: '7px 13px', color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', userSelect: 'none' }}
                  >
                    <span>🪙</span>
                    <span style={{ fontWeight: '700' }}>{tokenBalance}</span>
                    <span style={{ fontSize: '9px', color: '#777', marginLeft: '2px' }}>▼</span>
                  </div>
                  {tokenDropOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '8px', padding: '10px 0', minWidth: '160px', zIndex: 1001, boxShadow: '0 6px 20px rgba(0,0,0,0.5)' }}>
                      <div style={{ padding: '4px 14px 8px', borderBottom: '1px solid #2a2a2a', marginBottom: '4px' }}>
                        <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif' }}>Balance</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: T.white, fontFamily: 'Arial, sans-serif' }}>{tokenBalance} tokens</div>
                      </div>
                      <div
                        onClick={() => { setTokenDropOpen(false); navigate('/tokens/purchase'); }}
                        style={{ padding: '8px 14px', fontSize: '13px', color: T.accent, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontWeight: '700', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#262626'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        + Buy More Article Credits
                      </div>
                    </div>
                  )}
                </div>
              )}
              {siloHref && (
                <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '9px 18px', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: 'Arial, sans-serif', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
                  {userType === 'company' ? 'Admin Page' : 'My Silo'}
                </Link>
              )}
            </>
          ) : (
            <>
              <Link to="/login"
                style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = T.white; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
              >
                Login
              </Link>
              <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
                Get Started
              </Link>
            </>
          )}
        </div>

        {/* Links strip — center column only, aligns with content area */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F', borderBottom: '2px solid #000' }}>
          {GENRE_ROUTES.map(({ label, to, color }) => (
            <Link key={label} to={to}
              style={{ color: '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', transition: 'background 0.12s, color 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = T.white; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; }}
            >
              {label}
            </Link>
          ))}
        </div>
      </nav>
      )}

      {/* ── Mobile Body ── */}
      {isMobile && (
        <div style={{ padding: '12px' }}>
          {loading
            ? [...Array(6)].map((_, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '14px', paddingBottom: '14px', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ width: '90px', height: '68px', background: T.lightBg, flexShrink: 0, borderRadius: '3px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: '10px', background: T.lightBg, borderRadius: '2px', marginBottom: '6px', width: '40%' }} />
                    <div style={{ height: '14px', background: T.lightBg, borderRadius: '2px', marginBottom: '5px' }} />
                    <div style={{ height: '14px', background: T.lightBg, borderRadius: '2px', width: '70%' }} />
                  </div>
                </div>
              ))
            : allArticles.map(a => {
                const color = { 'National News': '#dd3333', 'State News': '#4db2ec', 'Local News': '#27ae60', 'Sports': '#e67e22', 'Technology': '#8e44ad' }[a.genre_tree?.main] || T.accent;
                return (
                  <div key={a._id} onClick={() => go(a)} style={{ display: 'flex', gap: '10px', marginBottom: '14px', paddingBottom: '14px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
                    {a.headline_image && (
                      <div style={{ width: '90px', height: '68px', flexShrink: 0, overflow: 'hidden', borderRadius: '3px' }}>
                        <img src={a.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px', fontFamily: 'Arial, sans-serif' }}>
                        {a.genre_tree?.main}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: T.headText, lineHeight: '19px', fontFamily: 'Georgia, serif', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {a.title}
                      </div>
                      <div style={{ fontSize: '11px', color: T.meta, marginTop: '4px', fontFamily: 'Arial, sans-serif' }}>
                        {a.company_name} · {new Date(a.date_created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                );
              })
          }
        </div>
      )}

      {/* ── Mobile Footer ── */}
      {isMobile && (
        <footer style={{ background: '#13273F', color: T.white, padding: '32px 20px 0' }}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '36px', objectFit: 'contain', marginBottom: '14px' }} />
          <p style={{ fontSize: '13px', color: '#666', lineHeight: '21px', margin: '0 0 20px', fontFamily: 'Arial, sans-serif' }}>
            PubSilo connects publishers of all sizes with readers who value quality journalism.
          </p>
          <div style={{ fontSize: '12px', color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: '24px', marginBottom: '24px' }}>
            <div>📍 Tulsa, Oklahoma</div>
            <div>📧 hello@pubsilo.com</div>
          </div>
          <div style={{ borderTop: '1px solid #1e1e1e', paddingTop: '16px', paddingBottom: '16px' }}>
            <div style={{ background: '#080808', margin: '0 -20px', padding: '14px 20px', fontSize: '11px', color: '#444', fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
              © {now.getFullYear()} PubSilo, LLC All rights reserved.
            </div>
          </div>
        </footer>
      )}

      {/* ── Desktop Body ── */}
      {!isMobile && <div style={{ display: 'flex', alignItems: 'flex-start' }}>

        {/* Left sidebar: publisher list */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', padding: '0 12px 10px', fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, marginBottom: '8px', marginLeft: '10px', marginRight: '10px' }}>
            Publishers
          </div>
          {loading
            ? [1, 2, 3, 4, 5].map(n => (
                <div key={n} style={{ width: '200px', height: '60px', margin: '4px 10px', background: T.lightBg, borderRadius: '4px' }} />
              ))
            : (publishersExpanded ? companies : companies.slice(0, 3)).map((c, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/${encodeURIComponent(c.company_name)}`)}
                  style={{ width: '200px', minHeight: '60px', margin: '4px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', cursor: 'pointer', border: `1px solid ${T.border}`, borderRadius: '4px', boxSizing: 'border-box', background: '#fafafa', transition: 'background 0.13s, border-color 0.13s', gap: '8px' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fff5f5'; e.currentTarget.style.borderColor = T.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = T.border; }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: T.headText, fontFamily: 'Arial, sans-serif', wordBreak: 'break-word', lineHeight: '1.3' }}>
                      {c.company_name}
                    </div>
                    <div style={{ fontSize: '10px', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
                      {c.articleCount} article{c.articleCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))
          }
          {!loading && companies.length > 3 && (
            <button
              onClick={() => setPublishersExpanded(e => !e)}
              style={{ display: 'block', width: 'calc(100% - 20px)', margin: '4px 10px 0', padding: '5px', background: 'transparent', color: '#888', border: `1px solid ${T.border}`, borderRadius: '4px', fontWeight: 700, cursor: 'pointer', fontSize: '10px', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = '#888'; }}
            >
              {publishersExpanded ? '▲ Show Less' : '▼ View More'}
            </button>
          )}
          <div style={{ padding: '12px 10px 0' }}>
            <button
              onClick={openRec}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center', padding: '9px', background: '#13273F', color: '#ffffff', border: 'none', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.07em', borderRadius: '4px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
            >
              + Suggest a Site
            </button>
          </div>

        </div>

        {/* Center content */}
        <div style={{ flex: 1, minWidth: 0, padding: '20px 20px 48px' }}>
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
              <Skeleton h={600} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[1, 2, 3].map(n => <Skeleton key={n} h={185} />)}
              </div>
            </div>
          ) : (
            <>
              {/* Hero + 3 side articles */}
              {heroArticle && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  <HeroCard article={heroArticle} onClick={() => go(heroArticle)} />
                  <div style={{ height: '600px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {heroSideArticles.length > 0
                      ? heroSideArticles.map(a => <SideHeroCard key={a._id} article={a} onClick={() => go(a)} />)
                      : [1, 2, 3].map(n => <Skeleton key={n} h={185} />)
                    }
                  </div>
                </div>
              )}

              {/* Section divider */}
              <div style={{ borderTop: `3px solid ${T.headText}`, marginBottom: '22px' }} />

              {/* More Stories: 3-column grid */}
              <div style={{ marginBottom: '36px' }}>
                <div style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: T.headText, fontFamily: 'Arial, sans-serif', borderBottom: `2px solid ${T.accent}`, paddingBottom: '7px', marginBottom: '18px' }}>
                  More Stories
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '22px' }}>
                  {moreArticles.length > 0
                    ? moreArticles.map(a => <GridCard key={a._id} article={a} onClick={() => go(a)} />)
                    : [...Array(6)].map((_, i) => (
                        <div key={i}>
                          <Skeleton h={160} style={{ marginBottom: '10px' }} />
                          <Skeleton h={10} w="40%" style={{ marginBottom: '6px' }} />
                          <Skeleton h={15} w="90%" style={{ marginBottom: '5px' }} />
                          <Skeleton h={15} w="65%" />
                        </div>
                      ))
                  }
                </div>
              </div>

              {/* Trending: horizontal scroll */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: T.headText, fontFamily: 'Arial, sans-serif', borderBottom: `2px solid #e67e22`, paddingBottom: '7px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <span style={{ color: '#e67e22', fontSize: '11px' }}>▶</span> Trending
                </div>
                <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '10px', scrollbarWidth: 'thin', scrollbarColor: '#ddd transparent' }}>
                  {trendingArticles.length > 0
                    ? trendingArticles.map(a => <TrendCard key={a._id} article={a} onClick={() => go(a)} />)
                    : [...Array(6)].map((_, i) => (
                        <div key={i} style={{ width: '200px', flexShrink: 0 }}>
                          <Skeleton h={130} style={{ marginBottom: '8px' }} />
                          <Skeleton h={10} w="40%" style={{ marginBottom: '6px' }} />
                          <Skeleton h={14} w="90%" />
                        </div>
                      ))
                  }
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ width: '300px', flexShrink: 0, borderLeft: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '20px 16px' }}>

          {/* Local News */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ background: '#27ae60', padding: '8px 12px', marginBottom: '14px' }}>
              <span style={{ fontSize: '12px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif' }}>
                Local News
              </span>
            </div>
            {loading ? (
              [1, 2, 3].map(n => <div key={n} style={{ height: '55px', background: T.lightBg, borderRadius: '3px', marginBottom: '10px' }} />)
            ) : localArticles.length > 0 ? (
              <>
                {localArticles.map(a => <SideCard key={a._id} article={a} onClick={() => go(a)} />)}
                <Link to="/local-news" style={{ display: 'block', textAlign: 'center', fontSize: '11px', fontWeight: '800', color: '#27ae60', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif', marginTop: '4px' }}>
                  View All Local News →
                </Link>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: T.meta, fontFamily: 'Arial, sans-serif', lineHeight: '20px' }}>
                No local news available.
                <Link to="/local-news" style={{ display: 'block', fontSize: '12px', color: '#27ae60', textDecoration: 'none', marginTop: '6px' }}>
                  Browse local news →
                </Link>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: `1px solid ${T.border}`, marginBottom: '24px' }} />

          {/* Recommended */}
          <div>
            <div style={{ background: T.accent, padding: '8px 12px', marginBottom: '14px' }}>
              <span style={{ fontSize: '12px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif' }}>
                Recommended
              </span>
            </div>
            {!authToken ? (
              <div style={{ fontSize: '13px', color: T.meta, fontFamily: 'Arial, sans-serif', lineHeight: '20px' }}>
                <Link to="/login" style={{ color: T.accent, fontWeight: '700', textDecoration: 'none' }}>Log in</Link> to see personalized recommendations.
              </div>
            ) : forYouArticles.length > 0 ? (
              <>
                {forYouHasSignal === false && (
                  <div style={{ fontSize: '11px', color: T.meta, fontFamily: 'Arial, sans-serif', fontStyle: 'italic', marginBottom: '10px', lineHeight: '16px' }}>
                    Purchase articles or set interests to personalize your feed
                  </div>
                )}
                {forYouArticles.map(a => <SideCard key={a._id} article={a} onClick={() => go(a)} />)}
              </>
            ) : (
              <div style={{ fontSize: '13px', color: T.meta, fontFamily: 'Arial, sans-serif', lineHeight: '20px' }}>
                {userType === 'user'
                  ? 'Purchase articles or set interests to see personalized recommendations.'
                  : 'Personalized article recommendations for readers.'
                }
              </div>
            )}
          </div>

        </div>
      </div>}

      {/* ── Desktop Footer ── */}
      {!isMobile && <footer style={{ background: '#13273F', color: T.white, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '48px', padding: '52px 52px 32px' }}>

          {/* Left: company info */}
          <div>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '42px', objectFit: 'contain', marginBottom: '18px' }} />
          </div>

          {/* Center: generic links */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #1e1e1e', fontFamily: 'Arial, sans-serif' }}>
              Explore
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
              {[
                { label: 'About PubSilo',   to: null },
                { label: 'Privacy Policy',   to: '/privacy' },
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Help Center',      to: '/help' },
              ].map(({ label, to }) =>
                to ? (
                  <Link key={label} to={to}
                    style={{ fontSize: '13px', color: '#666', textDecoration: 'none', fontFamily: 'Arial, sans-serif', lineHeight: '28px', transition: 'color 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.color = T.white}
                    onMouseLeave={e => e.currentTarget.style.color = '#666'}
                  >{label}</Link>
                ) : (
                  <span key={label}
                    style={{ fontSize: '13px', color: '#666', cursor: 'default', fontFamily: 'Arial, sans-serif', lineHeight: '28px', transition: 'color 0.12s' }}
                    onMouseEnter={e => e.currentTarget.style.color = T.white}
                    onMouseLeave={e => e.currentTarget.style.color = '#666'}
                  >{label}</span>
                )
              )}
              <Link to="/company-contact" style={{ fontSize: '13px', color: '#666', fontFamily: 'Arial, sans-serif', lineHeight: '28px', textDecoration: 'none', transition: 'color 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.color = T.white}
                onMouseLeave={e => e.currentTarget.style.color = '#666'}
              >
                Become a Publisher
              </Link>
            </div>
          </div>

          {/* Right: contact + newsletter */}
          <div>
            <div style={{ fontSize: '11px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #1e1e1e', fontFamily: 'Arial, sans-serif' }}>
              Stay Connected
            </div>
            <div style={{ marginBottom: '22px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#ccc', marginBottom: '8px', fontFamily: 'Arial, sans-serif' }}>Contact</div>
              <div style={{ fontSize: '13px', color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: '23px' }}>
                <div>General: hello@pubsilo.com</div>
                <div>Advertising: ads@pubsilo.com</div>
                <div>Publishers: publishers@pubsilo.com</div>
              </div>
            </div>
            {authToken && (
              <div style={{ marginBottom: '22px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#ccc', marginBottom: '6px', fontFamily: 'Arial, sans-serif' }}>Newsletter</div>
                <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px', fontFamily: 'Arial, sans-serif' }}>Get top stories delivered to your inbox.</div>
                {newsletterDone ? (
                  <div style={{ fontSize: '13px', color: '#27ae60', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}>✓ You're subscribed!</div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        type="email"
                        value={newsletterEmail}
                        onChange={e => { setNewsletterEmail(e.target.value); setNewsletterError(''); }}
                        placeholder="your@email.com"
                        style={{ flex: 1, padding: '8px 10px', background: '#111', border: `1px solid ${newsletterError ? T.accent : '#2a2a2a'}`, borderRadius: '4px', color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', outline: 'none' }}
                      />
                      <button
                        type="button"
                        disabled={newsletterLoading}
                        onClick={async () => {
                          if (!newsletterEmail.trim()) { setNewsletterError('Enter your email.'); return; }
                          setNewsletterLoading(true); setNewsletterError('');
                          try {
                            await axios.post('/api/users/me/newsletter', { email: newsletterEmail.trim() }, { headers: { Authorization: `Bearer ${authToken}` } });
                            setNewsletterDone(true);
                          } catch (err) {
                            setNewsletterError(err.response?.data?.error || 'Something went wrong.');
                          } finally {
                            setNewsletterLoading(false);
                          }
                        }}
                        style={{ padding: '8px 14px', background: newsletterLoading ? '#555' : T.accent, color: T.white, border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '700', cursor: newsletterLoading ? 'not-allowed' : 'pointer', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}
                      >
                        {newsletterLoading ? '...' : 'Subscribe'}
                      </button>
                    </div>
                    {newsletterError && <div style={{ fontSize: '11px', color: T.accent, marginTop: '5px', fontFamily: 'Arial, sans-serif' }}>{newsletterError}</div>}
                  </>
                )}
              </div>
            )}
        
          </div>
        </div>

        {/* Copyright bar */}
        <div style={{ background: '#080808', borderTop: '1px solid #181818', padding: '16px 52px', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', color: '#444', fontFamily: 'Arial, sans-serif' }}>
            © {now.getFullYear()} PubSilo, LLC All rights reserved.
          </span>
        </div>
      </footer>}

    </div>

    {/* ── Suggest a Source modal ── */}
    {recOpen && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div style={{ background: '#fff', borderRadius: '14px', padding: '2rem', maxWidth: '460px', width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', fontFamily: 'Arial, sans-serif' }}>
          {!recSuccess ? (
            <>
              <div style={{ fontSize: '1.15rem', fontWeight: '800', color: '#111', marginBottom: '0.4rem' }}>Suggest a Source</div>
              <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                Know a news outlet, blog, or local publication you'd love to read on PubSilo?
                Send us a recommendation — if they become a PubSilo publisher, you'll earn
                Promotional Credits as a thank you.
              </p>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#444', marginBottom: '0.25rem' }}>Source Name</label>
              <input
                type="text"
                value={recName}
                onChange={e => setRecName(e.target.value)}
                placeholder="e.g. The Norman Transcript"
                style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', marginBottom: '0.75rem', boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#444', marginBottom: '0.25rem' }}>Website URL</label>
              <input
                type="url"
                value={recUrl}
                onChange={e => setRecUrl(e.target.value)}
                placeholder="e.g. https://normantranscript.com"
                style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', marginBottom: '0.75rem', boxSizing: 'border-box' }}
              />

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#444', marginBottom: '0.25rem' }}>Anything else? <span style={{ fontWeight: '400', color: '#999' }}>(optional)</span></label>
              <textarea
                value={recNote}
                onChange={e => setRecNote(e.target.value)}
                placeholder="Why do you like this source? Any contact info you know of?"
                rows={3}
                style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', marginBottom: '0.75rem', boxSizing: 'border-box', resize: 'vertical' }}
              />

              {recError && <p style={{ color: '#dc3545', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{recError}</p>}

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={submitRec}
                  disabled={recLoading}
                  style={{ flex: 1, padding: '0.6rem', background: T.accent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '800', fontSize: '0.88rem', cursor: recLoading ? 'default' : 'pointer', opacity: recLoading ? 0.7 : 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                  {recLoading ? 'Sending…' : 'Submit Recommendation'}
                </button>
                <button
                  onClick={() => setRecOpen(false)}
                  style={{ flex: 1, padding: '0.6rem', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎉</div>
                <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#111', marginBottom: '0.5rem' }}>Thanks for the tip!</div>
                <p style={{ fontSize: '0.88rem', color: '#555', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                  We'll look into it. If they join PubSilo, we'll send Promotional Credits your way as a thank you.
                </p>
                <button
                  onClick={() => setRecOpen(false)}
                  style={{ padding: '0.6rem 2rem', background: T.accent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '800', fontSize: '0.88rem', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
}
